import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { practitionerService } from '../../services/practitionerService';
import { supabase } from '../../lib/supabase';
import Footer from '../../components/Footer';
import Icon from '../../components/AppIcon';

const PractitionerAccessLinkGenerator = () => {
  const navigate = useNavigate();
  const { userProfile, loading: authLoading, signOut } = useAuth();
  
  const [practitioners, setPractitioners] = useState([]);
  const [selectedPractitioner, setSelectedPractitioner] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  
  // Access link generation
  const [generatingLink, setGeneratingLink] = useState(false);
  const [generatedCredentials, setGeneratedCredentials] = useState(null);
  const [expirationPeriod, setExpirationPeriod] = useState('7days');
  const [accessHistory, setAccessHistory] = useState([]);

  // Load practitioners on mount
  useEffect(() => {
    if (!authLoading && userProfile) {
      // Ensure user is super admin
      if (userProfile?.role !== 'super_admin') {
        navigate('/admin-login');
        return;
      }
      loadPractitioners();
    }
  }, [authLoading, userProfile]);

  const loadPractitioners = async () => {
    try {
      setLoading(true);
      setError('');
      const data = await practitionerService?.getAll();
      setPractitioners(data);
    } catch (err) {
      console.error('Error loading practitioners:', err);
      setError(err?.message || 'Erreur lors du chargement des praticiens');
    } finally {
      setLoading(false);
    }
  };

  const loadAccessHistory = async (practitionerId) => {
    try {
      // Get user profile for this practitioner
      const { data: userProfiles, error: profileError } = await supabase
        ?.from('user_profiles')
        ?.select('id, email, created_at')
        ?.eq('practitioner_id', practitionerId);

      if (profileError) throw profileError;

      // Mock access history (in production, you'd track this in a separate table)
      const history = userProfiles?.map(profile => ({
        id: profile?.id,
        email: profile?.email,
        lastLogin: profile?.created_at,
        status: 'active',
        loginCount: Math.floor(Math.random() * 50) + 1
      })) || [];

      setAccessHistory(history);
    } catch (err) {
      console.error('Error loading access history:', err);
    }
  };

  const handlePractitionerSelect = async (practitioner) => {
    setSelectedPractitioner(practitioner);
    setGeneratedCredentials(null);
    setSuccess('');
    setError('');
    await loadAccessHistory(practitioner?.id);
  };

  const generateAccessCredentials = async () => {
    if (!selectedPractitioner) return;

    try {
      setGeneratingLink(true);
      setError('');
      setSuccess('');

      // Generate random password
      let password = generateRandomPassword();
      const email = `${selectedPractitioner?.slug}@practitioner.endoreferral.fr`;
      const fullName = `${selectedPractitioner?.practitionerTitle} ${selectedPractitioner?.practitionerName}`;

      // Create user account
      const result = await practitionerService?.createUserAccount(
        email,
        password,
        fullName,
        selectedPractitioner?.id
      );

      if (result?.user) {
        // Generate dashboard access link
        const dashboardUrl = `${window.location?.origin}/individual-practitioner-dashboard`;
        
        setGeneratedCredentials({
          email,
          password,
          dashboardUrl,
          practitionerName: fullName,
          practiceName: selectedPractitioner?.practiceName,
          expiresAt: calculateExpirationDate(expirationPeriod),
          createdAt: new Date()?.toISOString()
        });

        setSuccess('Identifiants générés avec succès!');
        await loadAccessHistory(selectedPractitioner?.id);
      }
    } catch (err) {
      console.error('Error generating credentials:', err);
      setError(err?.message || 'Erreur lors de la génération des identifiants');
    } finally {
      setGeneratingLink(false);
    }
  };

  const generateRandomPassword = () => {
    const length = 12;
    const charset = 'abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789!@#$%^&*';
    let password = '';
    for (let i = 0; i < length; i++) {
      password += charset?.charAt(Math.floor(Math.random() * charset?.length));
    }
    return password;
  };

  const calculateExpirationDate = (period) => {
    const now = new Date();
    switch (period) {
      case '24hours':
        return new Date(now?.getTime() + 24 * 60 * 60 * 1000);
      case '7days':
        return new Date(now?.getTime() + 7 * 24 * 60 * 60 * 1000);
      case '30days':
        return new Date(now?.getTime() + 30 * 24 * 60 * 60 * 1000);
      case 'permanent':
        return null;
      default:
        return new Date(now?.getTime() + 7 * 24 * 60 * 60 * 1000);
    }
  };

  const copyToClipboard = (text) => {
    navigator?.clipboard?.writeText(text);
    setSuccess('Copié dans le presse-papiers!');
    setTimeout(() => setSuccess(''), 2000);
  };

  const sendEmailNotification = () => {
    if (!generatedCredentials) return;

    const subject = encodeURIComponent('Vos identifiants de connexion - EndoReferral');
    const body = encodeURIComponent(
      `Bonjour ${generatedCredentials?.practitionerName},\n\n` +
      `Vos identifiants de connexion pour votre tableau de bord EndoReferral :\n\n` +
      `URL de connexion : ${generatedCredentials?.dashboardUrl}\n` +
      `Email : ${generatedCredentials?.email}\n` +
      `Mot de passe : ${generatedCredentials?.password}\n\n` +
      `${generatedCredentials?.expiresAt ? `Ces identifiants expirent le ${new Date(generatedCredentials?.expiresAt)?.toLocaleDateString('fr-FR')}\n\n` : ''}` +
      `Cordialement,\n` +
      `L'équipe EndoReferral`
    );

    window.location.href = `mailto:${selectedPractitioner?.email}?subject=${subject}&body=${body}`;
  };

  const handleSignOut = async () => {
    try {
      await signOut();
      navigate('/admin-login');
    } catch (err) {
      console.error('Error signing out:', err);
    }
  };

  const filteredPractitioners = practitioners?.filter(p => {
    if (!searchTerm) return true;
    const term = searchTerm?.toLowerCase();
    return (
      p?.practiceName?.toLowerCase()?.includes(term) ||
      p?.practitionerName?.toLowerCase()?.includes(term) ||
      p?.email?.toLowerCase()?.includes(term)
    );
  });

  if (authLoading || loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 via-white to-blue-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Chargement...</p>
        </div>
      </div>
    );
  }

  return (
    <>
      <Helmet>
        <title>Générateur de Liens d'Accès - Super Admin</title>
        <meta name="description" content="Gestion des accès praticiens et génération de liens d'authentification" />
      </Helmet>

      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-blue-50">
        {/* Header */}
        <header className="bg-white/80 backdrop-blur-sm border-b border-gray-200/50 sticky top-0 z-40">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-2xl font-bold text-blue-600">Générateur de Liens d'Accès</h1>
                <p className="text-sm text-gray-600">Gestion des identifiants praticiens</p>
              </div>
              <div className="flex items-center gap-3">
                <button
                  onClick={() => navigate('/referral-management-dashboard')}
                  className="flex items-center gap-2 px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
                >
                  <Icon name="arrow-left" className="w-5 h-5" />
                  <span className="hidden sm:inline">Retour</span>
                </button>
                <button
                  onClick={handleSignOut}
                  className="flex items-center gap-2 px-4 py-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                >
                  <Icon name="log-out" className="w-5 h-5" />
                  <span className="hidden sm:inline">Déconnexion</span>
                </button>
              </div>
            </div>
          </div>
        </header>

        {/* Main Content */}
        <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          {/* Alerts */}
          {error && (
            <div className="mb-6 bg-red-50 border border-red-200 rounded-lg p-4 flex items-start gap-3">
              <Icon name="alert-circle" className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
              <p className="text-red-800">{error}</p>
            </div>
          )}

          {success && (
            <div className="mb-6 bg-green-50 border border-green-200 rounded-lg p-4 flex items-start gap-3">
              <Icon name="check-circle" className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
              <p className="text-green-800">{success}</p>
            </div>
          )}

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Practitioner Selection */}
            <div className="lg:col-span-1">
              <div className="bg-white/80 backdrop-blur-sm rounded-2xl shadow-xl border border-gray-200/50 p-6">
                <h2 className="text-lg font-semibold text-gray-900 mb-4">Sélectionner un Praticien</h2>
                
                {/* Search */}
                <div className="mb-4">
                  <div className="relative">
                    <Icon name="search" className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                    <input
                      type="text"
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e?.target?.value)}
                      placeholder="Rechercher..."
                      className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>
                </div>

                {/* Practitioners List */}
                <div className="space-y-2 max-h-96 overflow-y-auto">
                  {filteredPractitioners?.map(practitioner => (
                    <button
                      key={practitioner?.id}
                      onClick={() => handlePractitionerSelect(practitioner)}
                      className={`w-full text-left p-3 rounded-lg border transition-all ${
                        selectedPractitioner?.id === practitioner?.id
                          ? 'bg-blue-50 border-blue-300 shadow-sm'
                          : 'bg-white border-gray-200 hover:border-blue-200 hover:bg-blue-50/50'
                      }`}
                    >
                      <p className="font-medium text-gray-900">{practitioner?.practiceName}</p>
                      <p className="text-sm text-gray-600">
                        {practitioner?.practitionerTitle} {practitioner?.practitionerName}
                      </p>
                      <p className="text-xs text-gray-500 mt-1">{practitioner?.email}</p>
                    </button>
                  ))}
                </div>
              </div>
            </div>

            {/* Credential Generation */}
            <div className="lg:col-span-2">
              {!selectedPractitioner ? (
                <div className="bg-white/80 backdrop-blur-sm rounded-2xl shadow-xl border border-gray-200/50 p-12 text-center">
                  <Icon name="user-check" className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                  <p className="text-gray-500">Sélectionnez un praticien pour générer ses identifiants d'accès</p>
                </div>
              ) : (
                <div className="space-y-6">
                  {/* Current Access Status */}
                  <div className="bg-white/80 backdrop-blur-sm rounded-2xl shadow-xl border border-gray-200/50 p-6">
                    <h2 className="text-lg font-semibold text-gray-900 mb-4">Statut d'Accès Actuel</h2>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div className="bg-blue-50 rounded-lg p-4">
                        <p className="text-sm text-gray-600 mb-1">Comptes actifs</p>
                        <p className="text-2xl font-bold text-blue-600">{accessHistory?.length}</p>
                      </div>
                      <div className="bg-green-50 rounded-lg p-4">
                        <p className="text-sm text-gray-600 mb-1">Dernière connexion</p>
                        <p className="text-sm font-medium text-green-600">
                          {accessHistory?.[0]?.lastLogin 
                            ? new Date(accessHistory?.[0]?.lastLogin)?.toLocaleDateString('fr-FR')
                            : 'Jamais'
                          }
                        </p>
                      </div>
                      <div className="bg-purple-50 rounded-lg p-4">
                        <p className="text-sm text-gray-600 mb-1">Connexions totales</p>
                        <p className="text-2xl font-bold text-purple-600">
                          {accessHistory?.reduce((sum, h) => sum + h?.loginCount, 0)}
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Credential Generation Form */}
                  <div className="bg-white/80 backdrop-blur-sm rounded-2xl shadow-xl border border-gray-200/50 p-6">
                    <h2 className="text-lg font-semibold text-gray-900 mb-4">Générer Nouveaux Identifiants</h2>
                    
                    <div className="space-y-4">
                      {/* Expiration Period */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Période d'expiration
                        </label>
                        <select
                          value={expirationPeriod}
                          onChange={(e) => setExpirationPeriod(e?.target?.value)}
                          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        >
                          <option value="24hours">24 heures</option>
                          <option value="7days">7 jours</option>
                          <option value="30days">30 jours</option>
                          <option value="permanent">Permanent</option>
                        </select>
                      </div>

                      {/* Generate Button */}
                      <button
                        onClick={generateAccessCredentials}
                        disabled={generatingLink}
                        className="w-full flex items-center justify-center gap-2 px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {generatingLink ? (
                          <>
                            <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                            <span>Génération en cours...</span>
                          </>
                        ) : (
                          <>
                            <Icon name="key" className="w-5 h-5" />
                            <span>Générer les Identifiants</span>
                          </>
                        )}
                      </button>
                    </div>
                  </div>

                  {/* Generated Credentials Display */}
                  {generatedCredentials && (
                    <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-2xl shadow-xl border border-blue-200 p-6">
                      <div className="flex items-center gap-3 mb-4">
                        <div className="w-10 h-10 bg-blue-600 rounded-full flex items-center justify-center">
                          <Icon name="check" className="w-6 h-6 text-white" />
                        </div>
                        <div>
                          <h3 className="text-lg font-semibold text-gray-900">Identifiants Générés</h3>
                          <p className="text-sm text-gray-600">Partagez ces informations avec le praticien</p>
                        </div>
                      </div>

                      <div className="space-y-3">
                        {/* Dashboard URL */}
                        <div className="bg-white rounded-lg p-4">
                          <label className="block text-xs font-medium text-gray-600 mb-1">URL du Tableau de Bord</label>
                          <div className="flex items-center gap-2">
                            <input
                              type="text"
                              value={generatedCredentials?.dashboardUrl}
                              readOnly
                              className="flex-1 px-3 py-2 bg-gray-50 border border-gray-200 rounded text-sm"
                            />
                            <button
                              onClick={() => copyToClipboard(generatedCredentials?.dashboardUrl)}
                              className="px-3 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors"
                            >
                              <Icon name="copy" className="w-4 h-4" />
                            </button>
                          </div>
                        </div>

                        {/* Email */}
                        <div className="bg-white rounded-lg p-4">
                          <label className="block text-xs font-medium text-gray-600 mb-1">Email</label>
                          <div className="flex items-center gap-2">
                            <input
                              type="text"
                              value={generatedCredentials?.email}
                              readOnly
                              className="flex-1 px-3 py-2 bg-gray-50 border border-gray-200 rounded text-sm"
                            />
                            <button
                              onClick={() => copyToClipboard(generatedCredentials?.email)}
                              className="px-3 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors"
                            >
                              <Icon name="copy" className="w-4 h-4" />
                            </button>
                          </div>
                        </div>

                        {/* Password */}
                        <div className="bg-white rounded-lg p-4">
                          <label className="block text-xs font-medium text-gray-600 mb-1">Mot de Passe</label>
                          <div className="flex items-center gap-2">
                            <input
                              type="text"
                              value={generatedCredentials?.password}
                              readOnly
                              className="flex-1 px-3 py-2 bg-gray-50 border border-gray-200 rounded text-sm font-mono"
                            />
                            <button
                              onClick={() => copyToClipboard(generatedCredentials?.password)}
                              className="px-3 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors"
                            >
                              <Icon name="copy" className="w-4 h-4" />
                            </button>
                          </div>
                        </div>

                        {/* Expiration */}
                        {generatedCredentials?.expiresAt && (
                          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
                            <p className="text-sm text-yellow-800">
                              <Icon name="clock" className="w-4 h-4 inline mr-1" />
                              Expire le {new Date(generatedCredentials?.expiresAt)?.toLocaleDateString('fr-FR')} à {new Date(generatedCredentials?.expiresAt)?.toLocaleTimeString('fr-FR')}
                            </p>
                          </div>
                        )}
                      </div>

                      {/* Actions */}
                      <div className="mt-4 flex gap-3">
                        <button
                          onClick={sendEmailNotification}
                          className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-white border border-blue-300 text-blue-600 rounded-lg hover:bg-blue-50 transition-colors"
                        >
                          <Icon name="mail" className="w-5 h-5" />
                          <span>Envoyer par Email</span>
                        </button>
                      </div>
                    </div>
                  )}

                  {/* Access History */}
                  {accessHistory?.length > 0 && (
                    <div className="bg-white/80 backdrop-blur-sm rounded-2xl shadow-xl border border-gray-200/50 p-6">
                      <h2 className="text-lg font-semibold text-gray-900 mb-4">Historique des Accès</h2>
                      <div className="space-y-2">
                        {accessHistory?.map(history => (
                          <div key={history?.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                            <div>
                              <p className="text-sm font-medium text-gray-900">{history?.email}</p>
                              <p className="text-xs text-gray-500">
                                Dernière connexion: {new Date(history?.lastLogin)?.toLocaleDateString('fr-FR')}
                              </p>
                            </div>
                            <div className="flex items-center gap-4">
                              <span className="text-sm text-gray-600">{history?.loginCount} connexions</span>
                              <span className={`px-2 py-1 text-xs rounded-full ${
                                history?.status === 'active' ?'bg-green-100 text-green-700' :'bg-gray-100 text-gray-700'
                              }`}>
                                {history?.status === 'active' ? 'Actif' : 'Inactif'}
                              </span>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </main>

        <Footer />
      </div>
    </>
  );
};

export default PractitionerAccessLinkGenerator;